/**
 * ════════════════════════════════════════════════════════════════════
 * ★★★ 체험하기(일반 성인용). 절대 인증 가드를 붙이지 마라 ★★★
 * ════════════════════════════════════════════════════════════════════
 * 이 화면은 회사 정책상 로그인 없이 들어오는 공개 페이지다(체험하기 무인증 원칙).
 * `useAuthGuard` 같은 인증 가드를 이 파일에 추가하면 정책 위반이다.
 * 과거에 인증 가드를 여러 페이지에 한꺼번에 적용하는 작업에 이 페이지가 휩쓸려
 * 들어가 로그인이 강제된 사고가 있었다. 가드를 일괄 적용하는 작업을 할 때는
 * 이 파일(`play1.tsx`)과 `play.tsx`를 반드시 명시적으로 제외해야 한다.
 * BE 쪽도 같은 원칙으로 `/app/exhibition/**`를 인증 없이 통과시킨다(security-ignore-uris).
 *
 * 화면: 교사·직장인 등 일반 성인 대상 진단 체험(컴포넌트명은 `Exhibition`). URL `/play1`.
 * 흐름: 외부 홍보 링크/QR 등으로 직접 진입 → 개인정보 수집 동의 → 인적사항(이름·직업·소속·
 * 연락처·이메일) 입력 → 문항 응답(Q1~12 단일 선택, Q13은 6개 역량 순위 매기기) → 제출(POST)
 * → 결과 화면(해봄랜드 유형) + 만족도 설문 → 설문 제출(PATCH) 완료 후 "홈으로 이동" 버튼으로
 * `/`로 나간다.
 * 데이터: `@/fetcher/usePostExhibition`의 `postExhibition`/`patchExhibitionSurvey` 사용
 * (학생용 `postPlay`/`patchPlaySurvey`와는 다른 엔드포인트다, `play.tsx` 참고). 문항 데이터는
 * `@/constants/exhibition`에 하드코딩.
 * 이 화면은 로그인 페이지들과 달리 sessionStorage 토큰을 아예 참조하지 않는다.
 */
import React, { useState, useEffect, useLayoutEffect } from "react";
import Header from "@/components/layout/Header";
// import Footer from "@/components/home/Footer"; // Removed as it was causing compilation error
import { EXHIBITION_QUESTIONS, CHARACTER_DATA, REPRESENTATIVE_QUESTIONS, COMPETENCY_MAPPING } from "@/constants/exhibition";
import Head from "next/head";
import { postExhibition, patchExhibitionSurvey } from "@/fetcher/usePostExhibition";
import { FCType } from "@/types/exhibition";
import { formatPhoneAsYouType, toPhoneDigits } from "@/utils/formatPhoneAsYouType";

/** 개인정보 동의 → 인적사항 입력 → 13문항 응답 → 결과·설문까지 한 컴포넌트가 화면 단계를 분기 렌더한다. */
const Exhibition = () => {
    // 체험하기, 비로그인 공개 페이지. BE도 /app/exhibition/** 인증 면제(security-ignore-uris).
    const [userInfo, setUserInfo] = useState({
        name: "",
        job: "",
        affiliation: "",
        phone: "",
        email: "",
    });
    const [answers, setAnswers] = useState<Record<number, number>>({});
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [marketingAgreed, setMarketingAgreed] = useState(false);
    const [showPrivacyDetail, setShowPrivacyDetail] = useState(false);
    const [showMarketingDetail, setShowMarketingDetail] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [finalSubmitted, setFinalSubmitted] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [submissionId, setSubmissionId] = useState<number | null>(null);
    const [ranking, setRanking] = useState<string[]>([]); // Q13 순위 저장 (역량 약어 리스트)

    // 설문 상태
    const [survey, setSurvey] = useState({
        rating: 0,
        feedback: "",
    });
    const [hoverRating, setHoverRating] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // 제출 시 발견한 첫 빈 필수 항목의 DOM id. 해당 항목에 빨간 강조 ring 표시. 입력하면 해제.
    const [highlightId, setHighlightId] = useState<string | null>(null);

    // 진단 결과 화면으로 전환 시 스크롤 최상단 이동
    useLayoutEffect(() => {
        if (submitted && !finalSubmitted) {
            window.scrollTo(0, 0);
        }
    }, [submitted]);

    // 최종 완료 화면으로 전환 시 스크롤 최상단 이동
    useLayoutEffect(() => {
        if (finalSubmitted) {
            window.scrollTo(0, 0);
        }
    }, [finalSubmitted]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let { name, value } = e.target;

        // 전화번호 상태는 숫자만(서버 형식). 화면에서만 하이픈을 붙여 보여 준다.
        if (name === "phone") {
            value = toPhoneDigits(value);
        }

        setUserInfo((prev) => ({ ...prev, [name]: value }));
        setHighlightId(null);
    };

    const handleOptionChange = (questionId: number, optionId: number) => {
        setHighlightId(null);
        if (questionId === 13) {
            const q13 = EXHIBITION_QUESTIONS[12];
            const selectedOption = q13.options.find(opt => opt.id === optionId);
            const comp = (selectedOption as any)?.competency;

            if (!comp) return;

            setRanking(prev => {
                if (prev.includes(comp)) {
                    // 역순 취소: 가장 마지막으로 선택된 항목인 경우에만 취소 가능
                    if (prev[prev.length - 1] === comp) {
                        return prev.slice(0, -1);
                    }
                    return prev;
                } else {
                    if (prev.length >= 6) return prev;
                    return [...prev, comp];
                }
            });
        } else {
            setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
        }
    };

    const handleResetRanking = () => {
        setRanking([]);
    };

    const isOptionSelected = (questionId: number, optionId: number) => {
        if (questionId === 13) {
            const q13 = EXHIBITION_QUESTIONS[12];
            const selectedOption = q13.options.find(opt => opt.id === optionId);
            const comp = (selectedOption as any)?.competency;
            return ranking.includes(comp);
        }
        return answers[questionId] === optionId;
    };

    const getRankingNumber = (optionId: number) => {
        const q13 = EXHIBITION_QUESTIONS[12];
        const selectedOption = q13.options.find(opt => opt.id === optionId);
        const comp = (selectedOption as any)?.competency;
        const index = ranking.indexOf(comp);
        return index !== -1 ? index + 1 : null;
    };

    const isFormValid = () => {
        const { name, job, affiliation, phone, email } = userInfo;

        const isNameEntered = name.trim().length > 0;
        const isJobEntered = job.trim().length > 0;
        const isAffiliationEntered = affiliation.trim().length > 0;

        const phoneRegex = /^\d{11}$/;
        const isPhoneValid = phoneRegex.test(phone);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isEmailValid = !email || emailRegex.test(email);

        const q1To12Answered = EXHIBITION_QUESTIONS.slice(0, 12).every((q) => answers[q.id]);
        const q13Answered = ranking.length === 6;

        return (
            privacyAgreed &&
            isNameEntered &&
            isJobEntered &&
            isAffiliationEntered &&
            isPhoneValid &&
            isEmailValid &&
            q1To12Answered &&
            q13Answered
        );
    };

    // 강조 ring 클래스. highlightId와 일치하는 항목에만 붙는다.
    const ringCls = (id: string) =>
        highlightId === id ? " ring-2 ring-error-line" : "";

    // 폼 순서(화면 위→아래)대로 첫 빈/유효하지 않은 필수 항목의 DOM id 반환. 없으면 null.
    const getFirstInvalidFieldId = (): string | null => {
        const { name, job, affiliation, phone, email } = userInfo;
        if (!privacyAgreed) return "field-privacy";
        if (!name.trim()) return "field-name";
        if (!job.trim()) return "field-job";
        if (!affiliation.trim()) return "field-affiliation";
        if (!/^\d{11}$/.test(phone)) return "field-phone";
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
            return "field-email";
        const firstUnanswered = EXHIBITION_QUESTIONS.slice(0, 12).find(
            (q) => !answers[q.id]
        );
        if (firstUnanswered) return `field-q-${firstUnanswered.id}`;
        if (ranking.length !== 6) return "field-q-13";
        return null;
    };

    // 해당 id 항목으로 부드럽게 스크롤(화면 중앙) + 입력 요소면 포커스.
    const scrollToField = (id: string) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
            el.focus({ preventScroll: true });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;
        // 빈 필수 항목이 있으면 제출 대신 그 위치로 이동·강조하고 멈춘다.
        const firstInvalid = getFirstInvalidFieldId();
        if (firstInvalid) {
            setHighlightId(firstInvalid);
            scrollToField(firstInvalid);
            return;
        }
        setIsSubmitting(true);

        // 1. 역량별 점수 합산 (Q1~Q12)
        const counts: Record<string, number> = { W: 0, C: 0, S: 0, M: 0, E: 0, T: 0 };
        const selectedOptionIds: number[] = [];

        EXHIBITION_QUESTIONS.slice(0, 12).forEach(q => {
            const selectedId = answers[q.id];
            selectedOptionIds.push(selectedId);
            const selectedOption = q.options.find(opt => opt.id === selectedId);
            if ((selectedOption as any)?.competency) {
                counts[(selectedOption as any).competency]++;
            }
        });

        // 2. 최대 점수 확인
        const maxScore = Math.max(...Object.values(counts));
        const winners = Object.keys(counts).filter(comp => counts[comp] === maxScore);

        let finalComp = winners[0];

        // 3. 동점자 발생 시 처리
        if (winners.length > 1) {
            // 동점자 처리 1: 대표 문항 정답 개수 비교
            const representativeScores = winners.map(comp => {
                const repQIds = REPRESENTATIVE_QUESTIONS[comp] || [];
                const score = repQIds.reduce((acc, qId) => {
                    const q = EXHIBITION_QUESTIONS.find(item => item.id === qId);
                    const selectedId = answers[qId];
                    const selectedOption = q?.options.find(opt => opt.id === selectedId);
                    return (selectedOption as any)?.competency === comp ? acc + 1 : acc;
                }, 0);
                return { comp, score };
            });

            const maxRepScore = Math.max(...representativeScores.map(s => s.score));
            const repWinners = representativeScores.filter(s => s.score === maxRepScore);

            if (repWinners.length === 1) {
                finalComp = repWinners[0].comp;
            } else {
                // 동점자 처리 2: Q13 순위 비교
                const winnerComps = repWinners.map(w => w.comp);
                const bestRankedComp = winnerComps.reduce((prev, curr) => {
                    const prevRank = ranking.indexOf(prev);
                    const currRank = ranking.indexOf(curr);
                    return currRank < prevRank ? curr : prev;
                });
                finalComp = bestRankedComp;
            }
        }

        const fcTypeKey = COMPETENCY_MAPPING[finalComp] as FCType;
        const topCompetency = CHARACTER_DATA[fcTypeKey];

        const finalResult = {
            userName: userInfo.name,
            date: new Date().toLocaleDateString(),
            competency: topCompetency
        };

        try {
            const initialSubmissionData = {
                name: userInfo.name.trim(),
                job: userInfo.job.trim(),
                affiliation: userInfo.affiliation.trim(),
                phone: userInfo.phone,
                email: userInfo.email.trim() || undefined,
                privacyAgreed: true,
                marketingAgreed: marketingAgreed,
                fcType: fcTypeKey,
                answers: [...selectedOptionIds, ...ranking.map(c => (EXHIBITION_QUESTIONS[12].options as any[]).find(o => o.competency === c)?.id || 0)],
                ranking: ranking,
                survey1: 0,
                survey2: "미제출"
            };

            const response = await postExhibition(initialSubmissionData);
            if (response && response.result) {
                setSubmissionId(response.result);
            }

            setResult(finalResult);
            setSubmitted(true);
        } catch (error) {
            console.error("Exhibition Initial Post Error:", error);
            alert("제출 중 오류가 발생했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFinalSubmit = async () => {
        if (isSubmitting || !submissionId) {
            if (!submissionId) alert("제출 번호를 찾을 수 없습니다. 다시 시도해주세요.");
            return;
        }
        setIsSubmitting(true);

        try {
            const surveyData = {
                survey1: survey.rating,
                survey2: survey.feedback.trim()
            };

            await patchExhibitionSurvey(submissionId, surveyData);
            console.log("Exhibition PATCH Success:", surveyData);
            setFinalSubmitted(true);
        } catch (error) {
            console.error("Exhibition PATCH Error:", error);
            alert("설문 제출 중 오류가 발생했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // 최종 완료 화면
    if (finalSubmitted) {
        return (
            <div key="final-success" className="bg-page min-h-screen">
                <Header />
                <main className="max-w-[1000px] mx-auto py-20 px-6">
                    <div className="bg-white rounded-[20px] p-10 shadow-sm text-center">
                        <img src="/images/exhibition/ppiyak.png" alt="Success" className="w-[300px] mx-auto mb-8" />
                        <h1 className="text-32 font-bold mb-4 font-heading">고생하셨습니다!</h1>
                        <p className="text-18 text-muted mb-12">체험과 설문에 응해주셔서 대단히 감사합니다.</p>

                        <button
                            onClick={() => window.location.href = "/"}
                            className="w-full bg-primary text-white py-4 rounded-[12px] text-18 font-bold hover:bg-primary-hover transition-colors font-heading"
                        >
                            홈으로 이동
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    // 진단 결과 + 설문 화면
    if (submitted && result) {
        return (
            <div className="bg-page min-h-screen">
                <Header />
                <main className="max-w-[1000px] mx-auto py-20 px-6">
                    <div className="bg-white rounded-[20px] overflow-hidden shadow-sm">
                        {/* 상단 이미지 */}
                        <img src="/images/exhibition/dqland_1.jpeg" alt="해봄랜드" className="w-full h-auto" />

                        <div className="p-10">
                            <h2 className="text-24 font-bold text-center mb-8 font-heading">
                                <span className="text-primary">{result.userName}</span>님의 길잡이별 검사 체험 결과
                            </h2>

                            <div className="mb-12">
                                <p className="text-16 text-muted text-center mb-10">진단일시: {result.date}</p>


                                <div className="border-b border-line pb-10 mb-12">
                                    <h3 className="text-22 font-bold mb-6 text-center font-heading">해봄랜드 유형</h3>
                                    <div className="-mx-10 flex justify-center">
                                        <img src={result.competency.image} alt={result.competency.name} className="w-full h-auto" />
                                    </div>
                                </div>

                                {/* 추가 설문 세션 */}
                                <div className="space-y-10">
                                    <div className="text-center">
                                        <h3 className="text-22 font-bold mb-2 font-heading">참여해주셔서 감사합니다! 해봄 성장을 위해 아래 설문에 답변 부탁드립니다 :) </h3>

                                    </div>

                                    <div className="space-y-8 bg-primary-soft rounded-[20px] p-8">
                                        <div className="space-y-4">
                                            <label className="text-18 font-bold text-strong block font-heading">
                                                1. 참여하신 길잡이별 검사 체험은 어땠나요?
                                            </label>
                                            <div className="flex gap-4 justify-center" onMouseLeave={() => setHoverRating(0)}>
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <button
                                                        key={star}
                                                        onClick={() => setSurvey(prev => ({ ...prev, rating: star }))}
                                                        onMouseEnter={() => setHoverRating(star)}
                                                        className={`font-heading text-24 font-bold transition-all transform hover:scale-110 ${(hoverRating || survey.rating) >= star ? 'text-warning' : 'text-gray-300'
                                                            }`}
                                                    >
                                                        ★
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <label className="text-18 font-bold text-strong block font-heading">
                                                2. 위와 같이 점수를 준 이유나, 검사를 하면서 느낀 점을 자유롭게 작성해주세요.
                                            </label>
                                            <div className="relative">
                                                <textarea
                                                    value={survey.feedback}
                                                    onChange={(e) => setSurvey(prev => ({ ...prev, feedback: e.target.value.slice(0, 1000) }))}
                                                    placeholder="의견을 남겨주세요."
                                                    maxLength={1000}
                                                    className="w-full h-32 p-4 rounded-[12px] border border-line focus:border-primary-focus focus:outline-none resize-none pb-10"
                                                />
                                                <div className="absolute bottom-3 right-4 text-12 text-disabled">
                                                    <span className={survey.feedback.length >= 1000 ? 'text-error font-bold' : ''}>
                                                        {survey.feedback.length}
                                                    </span>
                                                    /1000자
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleFinalSubmit}
                                disabled={survey.rating === 0 || !survey.feedback.trim() || isSubmitting}
                                className={`font-heading w-full py-4 rounded-[12px] text-18 font-bold transition-colors ${survey.rating > 0 && survey.feedback.trim() && !isSubmitting ? 'bg-primary text-white hover:bg-primary-hover' : 'bg-fill-strong text-disabled'
                                    }`}
                            >
                                {isSubmitting ? "제출 중..." : "제출하기"}
                            </button>
                            {(survey.rating === 0 || !survey.feedback.trim()) && (
                                <p className="text-center text-13 text-error mt-4">* 평점과 문항 2를 모두 입력해주세요.</p>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="bg-page min-h-screen">
            <Head>
                <title>해봄 체험하기 | 해봄</title>
            </Head>
            <Header />
            <main className="max-w-[1000px] mx-auto py-20 px-6">
                <div className="bg-white rounded-[20px] p-10 shadow-sm">
                    <div className="mb-10 text-center">
                        <h1 className="text-32 font-bold mb-4 font-heading">해봄 체험하기</h1>
                        <p className="text-16 text-muted">간단한 진단을 통해 AI시대 나의 미래 역량 유형을 확인해보세요!</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-12">
                        {/* 개인정보 수집 동의 */}
                        <div className="bg-page rounded-[12px] p-6 border border-line mb-6 space-y-4">
                            <div className="flex items-center justify-between gap-3 pb-4 border-b border-line">
                                <label className="flex items-center gap-3 cursor-pointer group flex-1">
                                    <input
                                        type="checkbox"
                                        checked={privacyAgreed && marketingAgreed}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setPrivacyAgreed(checked);
                                            setMarketingAgreed(checked);
                                            setHighlightId(null);
                                        }}
                                        className="w-5 h-5 rounded border-line text-primary focus:ring-primary-focus cursor-pointer"
                                    />
                                    <span className="text-16 font-bold text-strong group-hover:text-primary transition-colors">
                                        전체 동의합니다.
                                    </span>
                                </label>
                            </div>

                            <div
                                id="field-privacy"
                                className={`rounded-[8px] scroll-mt-24${highlightId === "field-privacy" ? " ring-2 ring-error-line" : ""}`}
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <label className="flex items-center gap-3 cursor-pointer group flex-1">
                                        <input
                                            type="checkbox"
                                            checked={privacyAgreed}
                                            onChange={(e) => { setPrivacyAgreed(e.target.checked); setHighlightId(null); }}
                                            className="w-5 h-5 rounded border-line text-primary focus:ring-primary-focus cursor-pointer"
                                        />
                                        <span className="text-15 text-body font-medium group-hover:text-primary transition-colors">
                                            [필수] 체험을 위한 인적사항 수집에 동의합니다.
                                        </span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setShowPrivacyDetail(v => !v)}
                                        className="text-13 text-primary font-medium underline-offset-2 hover:underline whitespace-nowrap"
                                    >
                                        {showPrivacyDetail ? "상세 닫기" : "상세보기"}
                                    </button>
                                </div>
                                {showPrivacyDetail && (
                                    <div className="mt-4 pt-4 border-t border-line-subtle text-13 text-body space-y-2 leading-relaxed">
                                        <p>㈜코드하나는 체험하기 채점 및 리포트 분석을 위해 아래와 같이 개인정보를 수집·이용합니다.</p>
                                        <p><span className="font-bold">· 수집 항목:</span> 이름, 직업, 소속, 연락처,  이메일(선택)</p>
                                        <p><span className="font-bold">· 수집 목적:</span> 해봄 체험 결과 분석 및 서비스 안내</p>
                                        <p><span className="font-bold">· 보유 및 이용기간:</span> 수집일로부터 2년 또는 동의 철회 시까지</p>
                                        <p><span className="font-bold">· 동의 거부 안내:</span> 본 동의를 거부하실 수 있으나, 거부 시 해봄 체험 서비스 이용이 제한됩니다.</p>
                                    </div>
                                )}
                            </div>

                            <div className="pt-4 border-t border-line-subtle">
                                <div className="flex items-center justify-between gap-3">
                                    <label className="flex items-center gap-3 cursor-pointer group flex-1">
                                        <input
                                            type="checkbox"
                                            checked={marketingAgreed}
                                            onChange={(e) => setMarketingAgreed(e.target.checked)}
                                            className="w-5 h-5 rounded border-line text-primary focus:ring-primary-focus cursor-pointer"
                                        />
                                        <span className="text-15 text-body font-medium group-hover:text-primary transition-colors">
                                            [선택] 마케팅 이벤트 수신에 동의합니다.
                                        </span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setShowMarketingDetail(v => !v)}
                                        className="text-13 text-primary font-medium underline-offset-2 hover:underline whitespace-nowrap"
                                    >
                                        {showMarketingDetail ? "상세 닫기" : "상세보기"}
                                    </button>
                                </div>
                                {showMarketingDetail && (
                                    <div className="mt-4 pt-4 border-t border-line-subtle text-13 text-body space-y-2 leading-relaxed">
                                        <p>㈜코드하나는 교육 프로그램, 행사, 이벤트 안내를 위해 아래와 같이 개인정보를 활용합니다.</p>
                                        <p><span className="font-bold">· 이용 항목:</span> 이름, 연락처, 이메일</p>
                                        <p><span className="font-bold">· 이용 목적:</span> 신규 프로그램, 행사, 이벤트 안내 및 홍보</p>
                                        <p><span className="font-bold">· 보유 기간:</span> 수집일로부터 2년 또는 동의 철회 시까지</p>
                                        <p><span className="font-bold">· 동의 거부 안내:</span> 귀하는 마케팅 정보 수신에 대한 동의를 거부할 수 있으며, 동의하지 않아도 서비스 이용에는 제한이 없습니다. 동의 거부 시 수집된 정보는 해봄 체험하기 채점 및 분석으로만 활용됩니다.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 인적사항 입력 */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-10 border-b border-line-subtle">
                            <div className="space-y-2">
                                <label className="text-15 font-bold text-body">
                                    이름 <span className="text-error">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="field-name"
                                    name="name"
                                    value={userInfo.name}
                                    onChange={handleInputChange}
                                    placeholder="예: 이해봄"
                                    required
                                    maxLength={20}
                                    className={`w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page scroll-mt-24${ringCls("field-name")}`}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-15 font-bold text-body">
                                    직업 <span className="text-error">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="field-job"
                                    name="job"
                                    value={userInfo.job}
                                    onChange={handleInputChange}
                                    placeholder="예: 교사, 학생, 학부모"
                                    required
                                    maxLength={20}
                                    className={`w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page scroll-mt-24${ringCls("field-job")}`}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-15 font-bold text-body">
                                    소속 <span className="text-error">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="field-affiliation"
                                    name="affiliation"
                                    value={userInfo.affiliation}
                                    onChange={handleInputChange}
                                    placeholder="예: 코하초등학교,코하대학교"
                                    required
                                    maxLength={50}
                                    className={`w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page scroll-mt-24${ringCls("field-affiliation")}`}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-15 font-bold text-body">
                                    전화번호 <span className="text-error">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="field-phone"
                                    name="phone"
                                    value={formatPhoneAsYouType(userInfo.phone)}
                                    onChange={handleInputChange}
                                    placeholder="010-1234-5678"
                                    required
                                    maxLength={13}
                                    className={`w-full px-4 py-3 rounded-[8px] border focus:outline-none bg-page transition-colors scroll-mt-24${ringCls("field-phone")} ${userInfo.phone && !/^\d{11}$/.test(userInfo.phone)
                                        ? 'border-error-line focus:border-error'
                                        : 'border-line focus:border-primary-focus'
                                        }`}
                                />
                                <p className={`text-12 ${userInfo.phone && !/^\d{11}$/.test(userInfo.phone) ? 'text-error' : 'text-disabled'}`}>
                                    * 전화번호 11자리를 입력해주세요. 숫자만 입력하면 - 가 자동으로 붙어요.
                                </p>
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <label className="text-15 font-bold text-body">이메일 (선택)</label>
                                <input
                                    type="email"
                                    id="field-email"
                                    name="email"
                                    value={userInfo.email}
                                    onChange={handleInputChange}
                                    placeholder="haebom@haebom.ai.kr"
                                    maxLength={100}
                                    className={`w-full px-4 py-3 rounded-[8px] border focus:outline-none bg-page transition-colors scroll-mt-24${ringCls("field-email")} ${userInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email)
                                        ? 'border-error-line focus:border-error'
                                        : 'border-line focus:border-primary-focus'
                                        }`}
                                />
                                {userInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email) && (
                                    <p className="text-12 text-error">* 올바른 이메일 형식을 입력해주세요.</p>
                                )}
                            </div>
                        </div>

                        {/* 문항 섹션 */}
                        <div className="space-y-10">
                            {EXHIBITION_QUESTIONS.map((q, idx) => (
                                <div
                                    key={q.id}
                                    id={`field-q-${q.id}`}
                                    className={`space-y-5 scroll-mt-24 rounded-[16px]${
                                        highlightId === `field-q-${q.id}`
                                            ? " ring-2 ring-error-line"
                                            : ""
                                    }`}
                                >
                                    <div className="flex justify-between items-end gap-2">
                                        <div className="space-y-2">
                                            <p className="text-14 font-bold text-primary mb-1">Q{idx + 1}</p>
                                            <h3 className="text-20 font-bold leading-tight text-strong whitespace-pre-wrap font-heading">
                                                {q.id === 13 ? (
                                                    <>
                                                        {q.content}
                                                        <span className="block mt-2 text-14 text-primary-400 font-normal">
                                                            * 중요하다고 생각하는 순서대로 번호를 눌러주세요.
                                                        </span>
                                                    </>
                                                ) : q.content}
                                            </h3>
                                        </div>
                                        {q.id === 13 && (
                                            <button
                                                type="button"
                                                onClick={handleResetRanking}
                                                disabled={ranking.length === 0}
                                                className={`px-4 py-2 text-14 font-bold rounded-[8px] transition-all mb-2 ${ranking.length > 0
                                                    ? 'bg-primary text-white hover:bg-primary-hover shadow-sm'
                                                    : 'bg-fill text-disabled cursor-not-allowed'
                                                    }`}
                                            >
                                                RESET
                                            </button>
                                        )}
                                    </div>

                                    <div className={`grid gap-3 ${q.id === 13 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                                        {q.options.map((option: any) => {
                                            const selected = isOptionSelected(q.id, option.id);
                                            const rankNum = q.id === 13 ? getRankingNumber(option.id) : null;

                                            return (
                                                <label
                                                    key={option.id}
                                                    onClick={(e) => {
                                                        if (q.id === 13) {
                                                            e.preventDefault();
                                                            handleOptionChange(q.id, option.id);
                                                        }
                                                    }}
                                                    className={`flex items-center gap-4 px-6 py-4 rounded-[16px] border-2 cursor-pointer transition-all ${selected
                                                        ? 'bg-primary-soft border-primary shadow-md'
                                                        : 'bg-white border-line-subtle hover:border-line-strong'
                                                        }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`q-${q.id}`}
                                                        value={option.id}
                                                        checked={selected}
                                                        onChange={() => handleOptionChange(q.id, option.id)}
                                                        className="hidden"
                                                    />
                                                    <div className={`w-9 h-9 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-16 font-black ${selected ? 'bg-primary border-primary text-white' : 'border-line text-gray-300'
                                                        }`}>
                                                        {q.id === 13 ? (rankNum || "-") : (q.options.indexOf(option) + 1)}
                                                    </div>
                                                    <span className={`text-17 leading-relaxed flex-1 whitespace-pre-wrap ${selected ? 'text-primary-ink font-bold' : 'text-body'}`}>
                                                        {option.text}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="pt-10">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`font-heading w-full py-5 rounded-[16px] text-20 font-bold transition-all bg-primary text-white shadow-lg ${isSubmitting
                                    ? 'opacity-60 cursor-wait'
                                    : 'hover:bg-primary-hover transform active:scale-[0.98] cursor-pointer'
                                    }`}
                            >
                                {isSubmitting ? "제출 중..." : "제출하고 결과 확인하기"}
                            </button>
                            {!isFormValid() && (
                                <p className="text-center text-13 text-error mt-4">
                                    * 모든 필수 정보를 입력하고 문제를 풀어주세요.
                                </p>
                            )}
                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
};

export default Exhibition;
